import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import dayjs from "dayjs";

import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "../board";

const getBrowserLanguage = () =>
  navigator.languages.find((language) => language.length === 2) as Language;

const defaultLanguages: Language[] = [
  "en",
  "en_US",
  "nl",
  "sr",
  "fr",
  "de",
  "pt",
  "es",
  "ja",
  "pl",
  "it",
];

interface LanguageContextValue {
  language: Language;
  languages: Language[];
  onChange: (language: Language) => void;
}

interface LanguageProviderProps {
  children: React.ReactNode;
  language?: Language;
  languages?: Language[];
}

const LanguageContext = React.createContext<LanguageContextValue>(null!);

const { Provider } = LanguageContext;

export const useLanguage = () => {
  const { language, onChange } = useContext(LanguageContext);

  return [language, onChange] as const;
};

export const useLanguages = () => {
  const { languages } = useContext(LanguageContext);

  return languages;
};

const isActivationScreen = window.location.pathname.includes("/activation");

export const LanguageProvider = React.memo<LanguageProviderProps>((props) => {
  const {
    children,
    language: defaultLanguage = isActivationScreen
      ? getBrowserLanguage()
      : "en",
    languages = defaultLanguages,
  } = props;

  const queryClient = useQueryClient();

  const { screenId } = useAppContext();

  const { i18n } = useTranslation();

  const [language, setLanguage] = useState<Language>(defaultLanguage);

  useEffect(() => {
    setLanguage(() => defaultLanguage);
  }, [defaultLanguage]);

  useEffect(() => {
    dayjs.locale(language);

    i18n.changeLanguage(language);
  }, [language]);

  const onChange = useCallback(
    (next: Language) => {
      setLanguage(next);
    },
    [i18n],
  );

  const onMessage = useCallback(async (event: MessageEvent<any>) => {
    const { data: rawMessage } = event;

    try {
      const message: ScreenMessage = JSON.parse(rawMessage);

      if (
        !(message && message.action && message.action === "update-language")
      ) {
        return;
      }

      const { data } = message;

      await queryClient.refetchQueries<{
        digitalReception: IDigitalReception;
      }>(["digitalReception", { id: screenId, language: data }]);

      setLanguage(data);
    } catch {
      //
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", onMessage);

    return () => window.removeEventListener("message", onMessage);
  }, []);

  const value = useMemo(
    () => ({
      language,
      languages,
      onChange,
    }),
    [languages, language, onChange],
  );

  return <Provider value={value}>{children}</Provider>;
});

export default LanguageContext;
